import dayjs from 'dayjs';

type Options = {
  /**
   * 預設等待時間 (毫秒)
   * @remarks 預設值：500 ms
   **/
  timeoutMs?: number;
  /**
   * 最大等待時間 (毫秒)
   * @remarks 預設值：3000 ms
   **/
  maxElapsedMs?: number;
};

type IOSSchemaParams = {
  domain: string;
  path: string;
  query?: string;
} & Partial<Options>;

type UseSchemaAppStoreFnReturn = {
  open: (params: IOSSchemaParams) => void;
  openApp: (params: IOSSchemaParams, storeUrl: string) => void;
  cleanup: () => void;
};

/**
 * 正規化 path 與 query 字串
 * @param params - 參數物件
 * @param params.path - 路徑
 * @param params.query - 查詢字串
 * @returns 正規化後的路徑與查詢字串
 */
const normalizePathAndQuery = (
  params: Pick<IOSSchemaParams, 'path' | 'query'>
): { normalizedPath: string; qs: string } => {
  const normalizedPath = params.path.startsWith('/') ? params.path : `/${params.path}`;
  const qs = params.query ? (params.query.startsWith('?') ? params.query : `?${params.query}`) : '';

  return { normalizedPath, qs };
};

/** Funday App Store 連結 */
const FD_APPLE_STORE_URL =
  'https://apps.apple.com/tw/app/funday%E8%8B%B1%E8%AA%9E%E5%AD%B8%E9%99%A2/id6670745600';

const FD_ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.funday.newfunday';

/**
 * 用於 iOS 開啟 Funday App 的 Composable
 * @param defaults - 預設選項
 * @remarks 用於開啟 Funday App，若失敗則導向 App Store
 */
export function useSchemaAppStoreFn(defaults?: Partial<Options>): UseSchemaAppStoreFnReturn {
  let timer: number | undefined;
  const appStore = useAppStore();

  /**
   * 清除 計時器與視窗頁面隱藏事件監聽器
   **/
  const cleanup = () => {
    if (timer) window.clearTimeout(timer);
    timer = undefined;
    window.removeEventListener('pagehide', onPageHide);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };

  /**
   * 視窗頁面隱藏時的處理函式
   */
  const onPageHide = (): void => cleanup();

  /**
   * 視窗頁面可見時的處理函式
   **/
  const onVisibilityChange = (): void => {
    if (document.hidden) cleanup();
  };

  /**
   * Open Funday App
   * @param params - 參數物件
   * @param params.domain - Funday App 的 domain
   * @param params.path - Funday App 的路徑
   * @param params.query - Funday App 的查詢字串
   * @param params.timeoutMs - 等待時間 (毫秒)
   * @param params.maxElapsedMs - 最大等待時間 (毫秒)
   * @param storeUrl - App Store 連結
   **/
  const openApp = (params: IOSSchemaParams, storeUrl: string): void => {
    // 防止連點造成多個 timer 疊加
    cleanup();

    const timeoutMs = params.timeoutMs ?? defaults?.timeoutMs ?? 500;
    const maxElapsedMs = params.maxElapsedMs ?? defaults?.maxElapsedMs ?? 3000;

    // 正規化 path 與 query
    const { normalizedPath, qs } = normalizePathAndQuery(params);

    // 組合 scheme URL
    const schemeUrl = `newfundayapp://${params.domain}${normalizedPath}${qs}`;

    // 記錄開始時間
    const startTime = dayjs().valueOf();

    window.addEventListener('pagehide', onPageHide, { once: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    timer = window.setTimeout(() => {
      // 計算已經過時間
      const elapsed: number = dayjs().valueOf() - startTime;

      // 使用者是否仍停留此視窗頁面
      const isStillHere = !document.hidden && elapsed < maxElapsedMs;

      cleanup();

      if (isStillHere) {
        window.location.assign(storeUrl);
      }
    }, timeoutMs);

    window.location.assign(schemeUrl);
  };

  /** Open Funday App (iOS | Android)
   * @param params - 參數物件
   * @param params.domain - Funday App 的 domain
   * @param params.path - Funday App 的路徑
   * @param params.query - Funday App 的查詢字串
   * @param params.timeoutMs - 等待時間 (毫秒)
   * @param params.maxElapsedMs - 最大等待時間 (毫秒)
   **/
  const open = (params: IOSSchemaParams) => {
    const storeUrl: string =
      appStore.mobileType === 'iOS' ? FD_APPLE_STORE_URL : FD_ANDROID_STORE_URL;
    openApp(params, storeUrl);
  };

  onBeforeUnmount(() => {
    cleanup();
  });

  return {
    open,
    openApp,
    cleanup
  };
}
